import React, { useState } from 'react';
import { Constants } from "@eagerdog/constants";
import { AxiosError } from "axios";

import { Modal, useModal } from "src/components/Modal/Modal";
import Refund from "src/components/Refund/Refund";
import EditTransaction from "src/components/EditTransaction/EditTransaction";
import ContextMenu from "src/components/ContextMenu/ContextMenu";
import Confirm from "src/components/Confirm/Confirm";
import { toast } from "src/components/Toast/ToastManager";
import { apiService } from "src/services/api.service";
import MarkAsPaidModal from "./MarkAsPaidModal";
import ArmbandAssignmentModal from "./ArmbandAssignmentModal";

import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./TransactionsTab.module.scss";

interface CustomTransactionCellRendererProps extends CustomCellRendererProps {
  getTransactions(): void
}

enum ModalType {
  REFUND = 'refund',
  EDIT = 'edit',
  PAYMENT_REQUEST = 'payment_request',
  MARK_PAID = 'mark_paid',
  ASSIGN_ARMBANDS = 'assign_armbands',
  CANCEL_PAYMENT_REQUEST = 'cancel_payment_request',
}

export const ActionsRenderer = (props: CustomTransactionCellRendererProps) => {
  const { isShown, toggle } = useModal();
  const [modalType, setModalType] = useState<ModalType>(ModalType.REFUND);

  const transaction = props.value?.transaction;

  if (!transaction) {
    return null;
  }

  const isUnpaid = transaction.status === Constants.transaction_status.unpaid;
  const isDisputed =
    transaction.status === Constants.transaction_status.disputed ||
    transaction.status === Constants.transaction_status.dispute_lost;
  const isCancelled = transaction.status === Constants.transaction_status.cancelled;
  const isPaymentRequested =
    transaction.status === Constants.transaction_status.payment_requested;
  // card AND bank_account are real Stripe charges; neither can be edited
  // (the API enforces the same rule in updateEventTransaction).
  const isStripePayment =
    transaction.amounts?.payment_method_type === Constants.payment_method_type.card ||
    transaction.amounts?.payment_method_type === Constants.payment_method_type.bank_account;

  // "Edit Transaction" (direct override, no online collection) is only offered for
  // offline transactions - a paid/settled Stripe transaction can't have its
  // recorded amount silently overwritten.
  const canEditTransaction =
    !isStripePayment && !isUnpaid && !isCancelled && !isPaymentRequested;

  // "Send Payment Request" (collect a shortfall online by card) is available for ANY
  // successfully-paid transaction, regardless of payment method - it never reuses the
  // original transaction's payment method, it always creates a fresh Stripe PaymentIntent.
  const canSendPaymentRequest =
    !isUnpaid &&
    !isCancelled &&
    !isPaymentRequested &&
    transaction.status === Constants.transaction_status.success;

  const menuOptions: any[] = [];

  if (isPaymentRequested) {
    menuOptions.push({
      label: "Re-send Payment Email",
      onClick: async () => {
        try {
          await apiService.resendPaymentRequest(
            props.value.event._id,
            transaction._id
          );
          toast.show({
            title: "Email Sent",
            content: "Payment request email has been re-sent.",
            duration: 5000,
            type: "success",
          });
        } catch (e: any) {
          toast.show({
            title: "Error",
            content:
              e.response?.data?.message || "Failed to re-send payment email.",
            duration: 8000,
            errorDetails: e as AxiosError,
            type: "fail",
          });
        }
      },
    });

    menuOptions.push({
      label: "Cancel Payment Request",
      onClick: () => {
        setModalType(ModalType.CANCEL_PAYMENT_REQUEST);
        toggle();
      },
    });
  }

  if (isUnpaid) {
    menuOptions.push({
      label: "Mark as Paid",
      onClick: () => {
        setModalType(ModalType.MARK_PAID);
        toggle();
      },
    });
    menuOptions.push({
      label: "Cancel Entry",
      onClick: async () => {
        try {
          await apiService.cancelUnpaidTransaction(
            props.value.event._id,
            transaction._id
          );
          toast.show({
            title: "Cancel Entry",
            content: "Entry has been cancelled.",
            duration: 5000,
            type: "success",
          });
          props.getTransactions();
        } catch (e: any) {
          toast.show({
            title: "Cancel Entry",
            content:
              e.response?.data?.message ||
              "Something went wrong. Please try again.",
            duration: 8000,
            errorDetails: e as AxiosError,
            type: "fail",
          });
        }
      },
    });
  }

  if (!isUnpaid && !isCancelled && !isPaymentRequested && !isDisputed) {
    menuOptions.push({
      label: "Refund Transaction",
      onClick: () => {
        setModalType(ModalType.REFUND);
        toggle();
      },
    });
  }

  if (canSendPaymentRequest) {
    menuOptions.unshift({
      label: "Send Payment Request",
      onClick: () => {
        setModalType(ModalType.PAYMENT_REQUEST);
        toggle();
      },
    });
  }

  if (canEditTransaction) {
    menuOptions.unshift({
      label: "Edit Transaction",
      onClick: () => {
        setModalType(ModalType.EDIT);
        toggle();
      },
    });
  }

  const getModalClassName = () => {
    switch (modalType) {
      case ModalType.MARK_PAID:
        return "markAsPaidModal";
      case ModalType.ASSIGN_ARMBANDS:
        return "armbandAssignmentModal";
      case ModalType.REFUND:
        return "refundModal";
      case ModalType.CANCEL_PAYMENT_REQUEST:
        return "confirmModal";
      default:
        return "editTransactionModal";
    }
  };

  const renderModalContent = () => {
    switch (modalType) {
      case ModalType.MARK_PAID:
        return (
          <MarkAsPaidModal
            transaction={transaction}
            event={props.value.event}
            eventId={props.value.event._id}
            onPaid={() => {
              setModalType(ModalType.ASSIGN_ARMBANDS);
            }}
            onCancel={() => {
              toggle();
            }}
          />
        );
      case ModalType.ASSIGN_ARMBANDS:
        return (
          <ArmbandAssignmentModal
            transaction={transaction}
            event={props.value.event}
            onFinish={() => {
              toggle();
              props.getTransactions();
            }}
            onSkip={() => {
              toggle();
              props.getTransactions();
            }}
          />
        );
      case ModalType.REFUND:
        return (
          <Refund
            type="event"
            transaction={transaction}
            onRefund={() => {
              toggle();
              props.getTransactions();
            }}
            onCancel={() => {
              toggle();
            }}
          />
        );
      case ModalType.CANCEL_PAYMENT_REQUEST:
        return (
          <Confirm
            message={
              <p>
                Are you sure you want to cancel this payment request? The
                registrant's payment link will become invalid.
              </p>
            }
            onConfirm={async () => {
              try {
                await apiService.cancelPaymentRequest(
                  props.value.event._id,
                  transaction._id
                );
                toast.show({
                  title: "Payment Request Cancelled",
                  content: "The payment request has been cancelled.",
                  duration: 5000,
                  type: "success",
                });
                toggle();
                props.getTransactions();
              } catch (e: any) {
                toast.show({
                  title: "Error",
                  content:
                    e.response?.data?.message ||
                    "Failed to cancel payment request.",
                  duration: 8000,
                  errorDetails: e as AxiosError,
                  type: "fail",
                });
              }
            }}
            onCancel={() => {
              toggle();
            }}
          />
        );
      case ModalType.PAYMENT_REQUEST:
        return (
          <EditTransaction
            transaction={transaction}
            eventId={props.value.event._id}
            mode="request"
            onFinish={() => {
              toggle();
              props.getTransactions();
            }}
            onCancel={() => {
              toggle();
            }}
          />
        );
      default:
        return (
          <EditTransaction
            transaction={transaction}
            eventId={props.value.event._id}
            mode="edit"
            onFinish={() => {
              toggle();
              props.getTransactions();
            }}
            onCancel={() => {
              toggle();
            }}
          />
        );
    }
  };

  return (
    <div className={styles.ActionRenderer}>
      <ContextMenu options={menuOptions} />
      <Modal
        className={getModalClassName()}
        isShown={isShown}
        hide={() => {
          toggle();
        }}
        modalContent={renderModalContent()}
      />
    </div>
  );
};
